import { useEffect, useRef, useState } from 'react';
import { Bot, Send, Loader2, Sparkles, User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import API, { apiError } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ErpHeading } from '../../components/erp/ErpShared';
import '../../components/erp/assets.css';

export default function CopilotPage() {
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [session, setSession] = useState(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);

  useEffect(() => { API.get('/erp/copilot/suggestions').then(r => setSuggestions(r.data.suggestions)).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  const send = async text => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setMessages(m => [...m, { role: 'user', content: q }]); setInput(''); setBusy(true); setError('');
    try {
      const r = await API.post('/erp/copilot/ask', { question: q, session_id: session });
      setSession(r.data.session_id);
      setMessages(m => [...m, { role: 'assistant', content: r.data.answer }]);
    } catch (e) { setError(apiError(e)); setMessages(m => [...m, { role: 'assistant', content: '⚠️ ' + apiError(e) }]); } finally { setBusy(false); }
  };

  return <div data-testid="copilot-page" className="flex flex-col h-[calc(100vh-220px)] min-h-[420px]">
    <ErpHeading title="Copilot AI"><span className="text-xs text-muted-foreground flex items-center gap-1"><Sparkles className="w-4 h-4" />Asistent operațional</span></ErpHeading>

    <div className="flex-1 overflow-y-auto border border-white/5 rounded-xl p-4 space-y-4" data-testid="copilot-thread">
      {!messages.length && <div className="text-center py-8">
        <Bot className="w-10 h-10 mx-auto text-blue-400 mb-3" />
        <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">Întreabă-mă despre proiecte, progres, facturare, stocuri, bugete sau termene. Am acces la datele operaționale curente.</p>
        <div className="flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
          {suggestions.map((s, i) => <button key={i} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-colors" data-testid={`copilot-suggestion-${i}`}>{s}</button>)}
        </div>
      </div>}
      {messages.map((m, i) => <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`} data-testid={`copilot-msg-${i}`}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${m.role === 'user' ? 'bg-primary/20 text-primary' : 'bg-blue-500/15 text-blue-300'}`}>{m.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}</div>
        <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.role === 'user' ? 'bg-primary/15' : 'bg-white/[0.03] border border-white/5'}`}>
          {m.role === 'assistant' ? <div className="prose prose-invert prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-headings:my-2"><ReactMarkdown>{m.content}</ReactMarkdown></div> : m.content}
        </div>
      </div>)}
      {busy && <div className="flex gap-3" data-testid="copilot-typing"><div className="w-8 h-8 rounded-full bg-blue-500/15 text-blue-300 flex items-center justify-center"><Bot className="w-4 h-4" /></div><div className="rounded-2xl px-4 py-2.5 bg-white/[0.03] border border-white/5"><Loader2 className="w-4 h-4 animate-spin" /></div></div>}
      <div ref={endRef} />
    </div>

    {error && <p className="text-xs text-rose-400 mt-2" data-testid="copilot-error">{error}</p>}
    <form onSubmit={e => { e.preventDefault(); send(); }} className="flex gap-2 mt-3">
      <Input value={input} onChange={e => setInput(e.target.value)} placeholder="Scrie o întrebare…" disabled={busy} data-testid="copilot-input" />
      <Button type="submit" disabled={busy || !input.trim()} data-testid="copilot-send"><Send className="w-4 h-4" /></Button>
    </form>
  </div>;
}
